import React, { ReactElement, useMemo } from 'react';
import { Text, ListRenderer } from '@feast-it/ui/light';

import { VisuallyHidden } from '../../../components/VisuallyHidden';
import { SliceLayout } from '../../../components/SliceLayout';
import { useIsDarkTheme } from '../../../context/DarkThemeContext';
import { StyledStatsGrid, StyledStatContent } from './StatsV2.styled';
import { getAccessibleMiddle, getVisualMiddle } from '../../../modules/stats';

interface AverageRating {
  rating: number;
  count: number;
}

interface Vertical {
  slug: string;
}

interface StatsV2Props {
  averageRating: AverageRating;
  bookingCount: number;
  quoteCount: number;
  isNearMe: boolean;
  vertical: Vertical;
  inLocation?: string;
}

interface StatItem {
  top: string;
  middle: string;
  bottom: string;
}

export function StatsV2({
  inLocation,
  averageRating,
  bookingCount,
  quoteCount,
  isNearMe,
  vertical,
}: StatsV2Props): ReactElement {
  const isVenue = vertical?.slug === 'venues';
  const verticalTitle = isVenue ? 'venues' : 'suppliers';
  const isDark = useIsDarkTheme();

  const items: StatItem[] = useMemo(
    () => [
      {
        top: `We've helped organise`,
        middle: `${bookingCount}`,
        bottom: `${bookingCount === 1 ? 'event' : 'events'} ${
          isNearMe ? '' : inLocation || ''
        }`,
      },
      {
        top: `Our ${verticalTitle} score`,
        middle: `${averageRating.rating.toFixed(1)}/5`,
        bottom: `on average ${isNearMe ? '' : inLocation || ''}`,
      },
      {
        top: 'Customers are reviewing',
        middle: `${quoteCount}`,
        bottom: `${quoteCount === 1 ? 'quote' : 'quotes'} for events ${
          isNearMe ? '' : inLocation || ''
        }`,
      },
    ],
    [
      bookingCount,
      averageRating.rating,
      quoteCount,
      verticalTitle,
      isNearMe,
      inLocation,
    ]
  );

  return (
    <SliceLayout backgroundColor="white">
      <VisuallyHidden>
        <h2>Statistics and Performance Metrics</h2>
      </VisuallyHidden>

      <StyledStatsGrid role="list" aria-label="Performance statistics">
        <ListRenderer
          items={items}
          render={({ item }) => {
            const accessibleSentence = `${item.top} ${getAccessibleMiddle(
              item.middle
            )} ${item.bottom}.`.replace(/\s+/g, ' ');
            const visualMiddle = getVisualMiddle(item.middle);

            return (
              <li>
                <VisuallyHidden>{accessibleSentence}</VisuallyHidden>

                <StyledStatContent aria-hidden="true">
                  <Text style="bodyMedium" color={isDark ? 'white' : 'black'}>
                    {item.top}
                  </Text>

                  <Text
                    style="display"
                    color={isDark ? 'paleYellow' : 'mediumBlue'}
                  >
                    {visualMiddle}
                  </Text>

                  <Text style="bodyMedium" color={isDark ? 'white' : 'black'}>
                    {item.bottom}
                  </Text>
                </StyledStatContent>
              </li>
            );
          }}
        />
      </StyledStatsGrid>
    </SliceLayout>
  );
}
